import { useEffect, useRef, useState } from 'react';

/**
 * Measures how long the most recent fetch took, for the shared load-time chip
 * (`<FormTitle loadMs=…>` / `<LoadTimeChip>`).  Pass a query's `isFetching`
 * flag; the hook times each fetch cycle (false → true → false) and returns the
 * last completed fetch's duration in ms (null until the first one finishes).
 *
 * Use this for TanStack-Query-backed grids that don't otherwise measure load
 * time, so every form surfaces the SAME top load-time indicator (CLAUDE.md
 * §18.6 — load-time at the top, one shared mechanism).
 *
 *   const { data, isFetching } = useRegions();
 *   const loadMs = useFetchMs(isFetching);
 *   …<FormTitle pageId="acc-region" loadMs={loadMs} loading={isFetching} />
 */
export function useFetchMs(active: boolean): number | null {
  const [ms, setMs] = useState<number | null>(null);
  const startRef = useRef<number | null>(null);
  const prev = useRef(false);
  useEffect(() => {
    if (active && !prev.current) startRef.current = performance.now();
    if (!active && prev.current && startRef.current != null) {
      setMs(Math.round(performance.now() - startRef.current));
    }
    prev.current = active;
  }, [active]);
  return ms;
}
